import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { updatesList } from '@/mocks/updates';
import { useSeo } from '@/hooks/useSeo';
import { formatDate } from '@/utils/date';
import Breadcrumb from '@/components/base/Breadcrumb';
import FavoriteButton from '@/components/base/FavoriteButton';
import ShareModal from '@/components/feature/ShareModal';

export default function UpdateDetail() {
  const { id } = useParams<{ id: string }>();
  const [shareOpen, setShareOpen] = useState(false);
  const update = useMemo(() => updatesList.find((item) => item.id === id), [id]);

  useSeo({
    title: update ? `版本更新V${update.version}详情 – 原神官方游戏站` : '版本更新详情 – 原神官方游戏站',
    description: update ? `${update.intro} 涵盖新角色、系统调整与活动开启等版本主要更新内容。` : '查看原神版本更新详情。',
    keywords: update ? `原神 ${update.version},版本更新,原神更新内容` : '原神版本更新',
    canonicalPath: `/updates/${id}`,
  });

  if (!update) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到该版本</h1>
        <Link
          to="/updates"
          className="mt-6 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-arrow-left-line"></i>
          返回版本列表
        </Link>
      </div>
    );
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const history = updatesList.filter((item) => update.history.includes(item.id));

  return (
    <div>
      <div className="relative h-[260px] w-full overflow-hidden md:h-[360px]">
        <img src={update.cover} alt={update.title} title={`${update.title} 原神版本更新`} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/30"></div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto w-full max-w-4xl px-4 pb-8 md:px-6">
            <div className="text-background-50/70">
              <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '版本更新', to: '/updates' }, { label: `V${update.version}` }]} />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-label text-foreground-950">V{update.version}</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-background-50/75 md:text-sm">
                <i className="ri-calendar-line"></i>
                {formatDate(update.date)}
              </span>
            </div>
            <h1 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-4xl">{update.title}</h1>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-wrap items-center gap-3 border-b border-background-200 pb-6">
          <FavoriteButton id={`update-${update.id}`} variant="full" />
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-label text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-share-line"></i>
            分享
          </button>
        </div>

        <p className="mt-8 rounded-lg border-l-4 border-primary-400 bg-primary-50 p-4 text-sm leading-relaxed text-foreground-700 md:text-base">
          {update.intro}
        </p>

        <div className="mt-10 space-y-10">
          {update.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                <a href={`#${section.heading}`}>{section.heading}</a>
              </h2>
              <ul className="mt-4 space-y-3">
                {section.items.map((entry) => (
                  <li key={entry} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-accent-600">
                      <i className="ri-checkbox-circle-line text-base"></i>
                    </span>
                    <span className="text-sm leading-relaxed text-foreground-700 md:text-base">{entry}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {history.length > 0 ? (
          <section className="mt-14">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              <a href="#history">相关版本历史</a>
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {history.map((item) => (
                <Link
                  key={item.id}
                  to={`/updates/${item.id}`}
                  className="gs-card-hover group overflow-hidden rounded-xl border border-background-200 bg-background-50"
                >
                  <div className="h-28 w-full overflow-hidden">
                    <img src={item.cover} alt={item.title} title={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-3.5">
                    <span className="text-[11px] font-label text-accent-700">V{item.version}</span>
                    <h3 className="mt-1 line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={`${update.title} V${update.version}`} url={currentUrl} />
    </div>
  );
}